import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Loading } from "@/components/Loading";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Account from "@/pages/Account";
import Create from "@/pages/Create";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Orders from "@/pages/Orders";
import OrderStatus from "@/pages/OrderStatus";
import Memory from "@/pages/Memory";
import Admin from "@/pages/Admin";
import FilmingTips from "@/pages/FilmingTips";
import BecomeAffiliate from "@/pages/BecomeAffiliate";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Examples from "@/pages/Examples";
import Share from "@/pages/Share";

const AuthCallback = () => {
  const done = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    const next = sessionStorage.getItem("v2r_next") || "/orders";
    sessionStorage.removeItem("v2r_next");
    api
      .post("/auth/google/session", { session_id: sid })
      .then(({ data }) => {
        setUser(data);
        navigate(data.role === "admin" && next === "/orders" ? "/admin" : next, { replace: true });
      })
      .catch(() => navigate("/login?error=google", { replace: true }));
  }, [navigate, setUser]);
  return <Loading text="Signing you in..." />;
};

const Protected = ({ children, admin }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  if (!user.phone && user.role !== "admin" && location.pathname !== "/account")
    return <Navigate to={`/account?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return children;
};

const bundleName = (html) => (html.match(/\/static\/js\/main\.[\w]+\.js/) || [])[0];

const UpdateChecker = () => {
  useEffect(() => {
    const mine = bundleName(document.documentElement.innerHTML);
    if (!mine) return undefined;
    const check = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const html = await (await fetch("/", { cache: "no-store" })).text();
        const live = bundleName(html);
        if (live && live !== mine && !window.__v2rUploading) window.location.reload();
      } catch { /* offline */ }
    };
    document.addEventListener("visibilitychange", check);
    const t = setInterval(check, 10 * 60 * 1000);
    return () => { document.removeEventListener("visibilitychange", check); clearInterval(t); };
  }, []);
  return null;
};

const RefTracker = () => {
  const location = useLocation();
  useEffect(() => {
    const ref = new URLSearchParams(location.search).get("ref");
    if (ref) api.post("/referrals/track", { code: ref }).catch(() => {});
  }, [location.search]);
  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
    if (location.pathname.startsWith("/admin")) return;
    let vid = localStorage.getItem("v2r_vid");
    if (!vid) { vid = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`; localStorage.setItem("v2r_vid", vid); }
    api.post("/visits", { visitor_id: vid, path: location.pathname, search: location.search || null, referrer: document.referrer || null }).catch(() => {});
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  const bare = location.pathname.startsWith("/m/");
  return (
    <>
      <RefTracker />
      <UpdateChecker />
      {!bare && <Header />}
      <main>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/tips" element={<FilmingTips />} />
          <Route path="/examples" element={<Examples />} />
          <Route path="/share" element={<Share />} />
          <Route path="/samples" element={<Navigate to="/examples" replace />} />
          <Route path="/affiliates" element={<BecomeAffiliate />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/m/:token" element={<Memory />} />
          <Route path="/account" element={<Protected><Account /></Protected>} />
          <Route path="/create" element={<Protected><Create /></Protected>} />
          <Route path="/create/:orderId" element={<Protected><Create /></Protected>} />
          <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
          <Route path="/orders" element={<Protected><Orders /></Protected>} />
          <Route path="/orders/:orderId" element={<Protected><OrderStatus /></Protected>} />
          <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!bare && <Footer />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors toastOptions={{ style: { fontSize: "1rem" } }} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
